import React, { FC, memo, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { GeoJSONSource, Map as MapboxMap, MapboxOptions } from 'mapbox-gl';
import { FeatureCollection, GeoJsonProperties, Point } from 'geojson';
import Script from 'next/script';
import { defaultConfiguration, mapInitconfiguration, messages, viaMapConfig } from '$components/elements/map/config';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ExtendedWindow, Viamap } from '$models/global-types';

type Icons = { standard: string; active: string } | string;

// Some weird error makes eslint think these aren't used
type MapProps = {
    displayPopup?: boolean;
    featureCollection?: FeatureCollection<Point>;
    fitToBounds?: boolean;
    markerPaths?: Icons;
    // Make sure id is supplied in GEOJson properties
    activeFeatureId?: string;
    onMarkerClick?: (properties: GeoJsonProperties) => void;
    useCluster?: boolean;
} & Partial<MapboxOptions>;

const Map: FC<PropsWithChildren<MapProps>> = memo((props: PropsWithChildren<MapProps>) => {
    const [map, setMap] = useState<MapboxMap | null>(null);
    const {
        featureCollection,
        fitToBounds = true,
        children,
        markerPaths,
        activeFeatureId,
        onMarkerClick,
        useCluster = false,
    } = { ...defaultConfiguration, ...props };

    /**
     * Update active marker with default configuration
     * this configuration will try to change the feature image to
     * an active feature. The active image need to be applied as props
     */
    const updateLayoutProperties = useCallback(
        (id: string, mapRef: MapboxMap) => {
            if (!id || typeof markerPaths !== 'object' || !markerPaths.active) {
                console.warn(messages.updateLayoutPropertiesError, `id: ${id}, markerPaths: ${markerPaths}`);
                return;
            }
            mapRef.setLayoutProperty(defaultConfiguration.layerId, 'icon-image', [
                'match',
                ['get', 'id'], // get the feature id (make sure your data has an id set or use generateIds for GeoJSON sources
                id,
                defaultConfiguration.activeIconKey, // active image which will be applied
                defaultConfiguration.iconKey, // default
            ]);
        },
        [markerPaths]
    );

    /**
     * Creates a layer on a map reference
     * with a default layer configuration, this configuration expects a icon
     * to create a marker configuration
     */
    const createLayer = useCallback((mapRef: MapboxMap, sourceId: string, iconKey: string) => {
        mapRef.addLayer({
            id: defaultConfiguration.layerId,
            type: 'symbol',
            source: sourceId,
            layout: {
                'icon-image': iconKey,
                'icon-size': 1,
                'icon-allow-overlap': true,
            },
        });

        // Make sure to show city names even though there is a marker
        mapRef.setLayoutProperty('place_label_city_town_medium', 'text-allow-overlap', true);
        mapRef.setLayoutProperty('place_label_city_big', 'text-allow-overlap', true);
    }, []);

    /**
     * Creates a source on a map reference
     * only one source can be created pr. id
     */
    const createSource = useCallback(
        (mapRef: MapboxMap, featureCollection?: FeatureCollection<Point>, activeFeatureId?: string) => {
            const { sourceId, iconKey } = defaultConfiguration;

            if (!featureCollection?.features?.length) {
                return;
            }

            mapRef.addSource(sourceId, {
                type: 'geojson',
                data: featureCollection,
                cluster: useCluster,
            });

            createLayer(mapRef, sourceId, iconKey);

            if (activeFeatureId) {
                updateLayoutProperties(activeFeatureId, mapRef);
            }
        },
        [useCluster, createLayer]
    );

    /**
     * Fit to all currently visible features
     */
    const fitBounds = useCallback((mapRef: MapboxMap, featureCollection?: FeatureCollection<Point>) => {
        if (!featureCollection?.features) {
            return;
        }

        const bounds = new window.mapboxgl.LngLatBounds();

        featureCollection.features.forEach(({ geometry: { coordinates } }) => {
            bounds.extend([coordinates[0], coordinates[1]]);
        });

        mapRef.fitBounds(bounds, { padding: 100 });
    }, []);

    /**
     * Fetch multiple or single image.
     * Expects object or string
     * after fetch images can be added to a source and used via a layer with the prop 'icon-image'
     */
    const loadImages = useCallback(async (paths: Icons, mapRef: MapboxMap, callBack: () => void) => {
        if (typeof paths === 'object') {
            const { standard, active } = paths;
            await Promise.all(
                [
                    { path: standard, key: defaultConfiguration.iconKey },
                    { path: active, key: defaultConfiguration.activeIconKey },
                ].map(
                    (img) =>
                        new Promise<void>((resolve) => {
                            if (!img.path) {
                                return;
                            }
                            mapRef.loadImage(img.path, (error?: Error, res?: ImageBitmap | HTMLImageElement) => {
                                if (res) {
                                    mapRef.addImage(img.key, res);
                                } else {
                                    console.warn(messages.imageLoadError, error);
                                }
                                resolve();
                            });
                        })
                )
            );
            callBack();
            return;
        }

        mapRef.loadImage(paths as string, (error, image) => {
            if (error) {
                console.warn(messages.imageLoadError, error);
            }

            if (image) {
                mapRef.addImage(defaultConfiguration.iconKey, image);
                callBack();
            }
        });
    }, []);

    const init = useCallback(async (vmsRef: Viamap) => {
        if (!vmsRef) {
            return;
        }

        const mapRef: MapboxMap = await vmsRef.initmap({
            ...mapInitconfiguration,
            ...defaultConfiguration,
            ...props,
        });

        vmsRef.load().then(() => {
            setMap(mapRef);

            mapRef.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
            mapRef.addControl(new window.mapboxgl.FullscreenControl(), 'top-left');

            if (!onMarkerClick) {
                return;
            }

            mapRef.on('click', defaultConfiguration.layerId, (e) => {
                if (e.features) {
                    onMarkerClick(e.features[0]?.properties);
                }
            });

            mapRef.on('mousemove', (e) => {
                if (!mapRef.getLayer(defaultConfiguration.layerId)) {
                    return;
                }

                const features = mapRef.queryRenderedFeatures(e.point, {
                    layers: [defaultConfiguration.layerId],
                });
                // Change the cursor to a pointer when hovering over a point on the map.
                mapRef.getCanvas().style.cursor = features.length ? 'pointer' : '';
            });
        });
    }, []);

    useEffect(() => {
        if (map?.getLayer(defaultConfiguration.layerId) && activeFeatureId) {
            updateLayoutProperties(activeFeatureId, map);
        }
    }, [activeFeatureId, map]);

    useEffect(() => {
        const vms = (window as ExtendedWindow).vms;

        if (vms) {
            init(vms);
        }

        return () => {
            if (window?.mapboxgl) {
                // Viamap Vistrap (3rd party provider) will try to set RTLText on the global instance on every init
                // which is fine, though mapbox only allow one call to this function and will error out on subsequently calls
                // we make sure the native function is not called on the instance again by overwriting it with a noop
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                window.mapboxgl.setRTLTextPlugin = () => {};
            }
            map?.remove();
        };
    }, []);

    useEffect(() => {
        if (!map || !featureCollection?.features.length) {
            return;
        }

        const { sourceId } = defaultConfiguration;
        const reference: GeoJSONSource = map.getSource(sourceId) as GeoJSONSource;
        fitToBounds && fitBounds(map, featureCollection);

        if (reference) {
            reference.setData(featureCollection);
            return;
        }

        if (markerPaths) {
            loadImages(markerPaths, map, () => createSource(map, featureCollection, activeFeatureId));
        }
    }, [map, featureCollection, createSource, fitBounds, fitToBounds]);

    return (
        <>
            <Script
                id="viastrap"
                src={viaMapConfig.strapScriptUrl}
                onLoad={() => init((window as ExtendedWindow)?.vms)}
            />
            {children}
        </>
    );
});

export default Map;
